/* Live class reactions — raise hand + emoji bursts.
 *
 * Used inside the live classroom (both teacher and students). Students get a
 * compact button strip that pushes reactions to the backend. Both sides poll
 * /api/live/{classId}/reactions every 2s and float incoming reactions briefly
 * onto the screen as animated emoji.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Hand, ThumbsUp, Heart, Laugh, Sparkles, Award, ChevronLeft } from "lucide-react";

const KINDS = [
  { id: "hand", label: "Raise hand", icon: Hand, color: "#FFD600" },
  { id: "thumb", label: "Like", icon: ThumbsUp, color: "#0EA5E9" },
  { id: "heart", label: "Love", icon: Heart, color: "#EF4444" },
  { id: "laugh", label: "Laugh", icon: Laugh, color: "#F59E0B" },
  { id: "wow", label: "Wow", icon: Sparkles, color: "#A855F7" },
  { id: "clap", label: "Clap", icon: Award, color: "#10B981" },
];
const KIND_META = Object.fromEntries(KINDS.map((k) => [k.id, k]));

/** Floating bursts shown to everyone in the class. Listens to reactions stream. */
export function ReactionsBurstLayer({ classId }) {
  const [bursts, setBursts] = useState([]); // { id, kind, user_name }
  const sinceRef = useRef(new Date().toISOString());

  useEffect(() => {
    if (!classId) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/reactions`, { params: { since: sinceRef.current } });
        const items = (data?.reactions || []).reverse(); // chronological
        if (items.length) {
          sinceRef.current = items[items.length - 1].created_at;
          if (!stop) {
            const fresh = items.map((r) => ({ id: r.id, kind: r.kind, user_name: r.user_name }));
            setBursts((b) => [...b, ...fresh].slice(-12));
            // auto-clear each after 4s
            fresh.forEach((f) => {
              setTimeout(() => setBursts((b) => b.filter((x) => x.id !== f.id)), 4200);
            });
          }
        }
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { stop = true; clearInterval(t); };
  }, [classId]);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-24 z-[80] flex justify-center" data-testid="reactions-burst">
      <div className="relative w-full max-w-3xl h-32">
        <AnimatePresence>
          {bursts.map((b, i) => {
            const meta = KIND_META[b.kind] || KINDS[0];
            const Icon = meta.icon;
            const xJitter = (i % 7) * 60 - 180;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 30, x: xJitter, scale: 0.6 }}
                animate={{ opacity: 1, y: -120, scale: 1.2 }}
                exit={{ opacity: 0, y: -200 }}
                transition={{ duration: 3.6, ease: "easeOut" }}
                className="absolute left-1/2 bottom-0 -translate-x-1/2 flex flex-col items-center"
                style={{ marginLeft: xJitter }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                  style={{ background: `${meta.color}28`, color: meta.color, border: `2px solid ${meta.color}` }}
                >
                  <Icon size={26} strokeWidth={2.2} />
                </div>
                <div className="mt-1 px-2 py-0.5 bg-black/70 text-white text-[10px] font-mono uppercase tracking-wider rounded-sm whitespace-nowrap">
                  {b.user_name?.split(" ")[0] || "Someone"}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** Compact reaction strip — fixed bottom-left, students primarily but works for both.
 *  Collapsible: users can tuck it away (persisted) if it overlaps other panels. */
export function ReactionsBar({ classId }) {
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("reactionsBar.hidden") === "1"; } catch { return false; }
  });
  const toggleHidden = () => {
    setHidden((v) => {
      const nv = !v;
      try { localStorage.setItem("reactionsBar.hidden", nv ? "1" : "0"); } catch { /* noop */ }
      return nv;
    });
  };
  const send = async (kind) => {
    setBusy(kind);
    try { await api.post(`/live/${classId}/reaction`, { kind }); }
    catch { /* ignore */ }
    finally { setTimeout(() => setBusy(null), 600); }
  };
  if (hidden) {
    return (
      <button
        onClick={toggleHidden}
        data-testid="reactions-bar-show"
        title="Show reactions"
        className="fixed bottom-5 left-5 z-[75] w-10 h-10 rounded-full bg-[#10141C] border border-white/10 shadow-lg flex items-center justify-center text-[#FFD600] hover:scale-110 transition-transform"
      >
        <Laugh size={17} />
      </button>
    );
  }
  return (
    <div
      className="fixed bottom-5 left-5 z-[75] flex items-center gap-1.5 bg-[#10141C] border border-white/10 rounded-full px-2 py-1.5 shadow-lg"
      data-testid="reactions-bar"
    >
      {KINDS.map((k) => {
        const Icon = k.icon;
        return (
          <button
            key={k.id}
            onClick={() => send(k.id)}
            disabled={!!busy}
            data-testid={`reaction-${k.id}`}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${busy === k.id ? "scale-125" : "hover:scale-110"}`}
            style={{ color: k.color }}
            title={k.label}
          ><Icon size={17} strokeWidth={2} /></button>
        );
      })}
      <button
        onClick={toggleHidden}
        data-testid="reactions-bar-hide"
        title="Hide reactions bar"
        className="w-7 h-7 rounded-full flex items-center justify-center text-neutral-500 hover:text-white hover:bg-white/10 transition-colors ml-0.5"
      >
        <ChevronLeft size={14} />
      </button>
    </div>
  );
}
